import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";

import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import {
  Fault,
  FaultPayload,
  FaultResponseDto,
  mapFaultResponse,
  toFaultPayload,
  WikiFault,
} from "../../../models/api/fault/fault";
import { FaultListObject, FaultList } from "../../../models/api/fault/faultList";
import { FaultListLanguage } from "../../../models/api/fault/faultListLanguage";
import { transformManufacturerName } from "../../../shared/utils/utils";
import { OffsetPaginationDto } from "../../../models/api/pagination";

export interface FaultWikiPageParams {
  page: number;
  pageSize: number;
  search?: string;
  manufacturerId?: string;
  modelId?: string;
  sortBy?: "faultCode" | "faultType" | "faultText" | "faultList";
  sortOrder?: "asc" | "desc";
}

@Injectable({
  providedIn: "root",
})
export class FaultListService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);

  /**
   * Gets all fault lists, creates FaultList objects and returns them sorted by label.
   * @returns {Observable<FaultList[]>} Observable with an array of FaultList objects, sorted by label.
   */
  public getFaultLists(): Observable<FaultList[]> {
    return this.http.get<FaultListObject[]>(environment.baseUrlV2 + "/faultList", {}).pipe(
      this.apiErrorHandler.handle("fault_list"),
      map((res) => {
        const faultList: FaultList[] = res.map((faultListObject) => {
          return new FaultList(faultListObject);
        });

        return this.orderByLabel(faultList);
      })
    );
  }

  /**
   * Gets all available languages for fault lists.
   * @returns {Observable<FaultListLanguage[]>} Observable with an array of FaultListLanguage objects.
   */
  public getFaultListLanguage(): Observable<FaultListLanguage[]> {
    return this.http
      .get<FaultListLanguage[]>(environment.baseUrlV2 + "/language", {})
      .pipe(this.apiErrorHandler.handle());
  }

  /**
   * Creates a new fault list language.
   * @param {FaultListLanguage} faultListLanguage The fault list language to create.
   * @returns {Observable<Object>} The response from the server as an Observable.
   */
  public postFaultListLanguage(faultListLanguage: FaultListLanguage) {
    return this.http.post(environment.baseUrlV2 + "/language", faultListLanguage);
  }

  /**
   * Updates a fault list language.
   * @param {string} faultListLanguageCode The code of the fault list language to update.
   * @param {FaultListLanguage} faultListLanguage The updated fault list language object.
   * @returns {Observable<Object>} The response from the server as an Observable.
   */
  public putFaultListLanguage(faultListLanguageCode: string, faultListLanguage: FaultListLanguage) {
    return this.http.put(environment.baseUrlV2 + "/language/" + faultListLanguageCode, faultListLanguage);
  }

  /**
   * Deletes a fault list language.
   * @param {string} faultListLanguageCode The code of the fault list language to delete.
   * @returns {Observable<Object>} The response from the server as an Observable.
   */
  public deleteFaultListLanguage(faultListLanguageCode: string) {
    return this.http.delete(environment.baseUrlV2 + "/language/" + faultListLanguageCode);
  }

  /**
   * Sorts an array of FaultList objects by their label.
   * @param {FaultList[]} faultLists The array of FaultList objects to sort.
   * @returns {FaultList[]} The sorted array.
   */
  private orderByLabel(faultLists: FaultList[]): FaultList[] {
    return faultLists.sort((a, b) => {
      if (a?.label && b?.label) {
        return a.label.localeCompare(b.label);
      } else {
        return 0;
      }
    });
  }

  /**
   * Gets paginated Fault Wiki rows from the backend.
   * @returns {Observable<OffsetPaginationDto<WikiFault>>} Paginated wiki faults.
   */
  public getFaultWikiData(params: FaultWikiPageParams): Observable<OffsetPaginationDto<WikiFault>> {
    let httpParams = new HttpParams().set("page", params.page).set("pageSize", params.pageSize);

    if (params.search?.trim()) httpParams = httpParams.set("search", params.search.trim());
    if (params.manufacturerId) httpParams = httpParams.set("manufacturerId", params.manufacturerId);
    if (params.modelId) httpParams = httpParams.set("modelId", params.modelId);
    if (params.sortBy) httpParams = httpParams.set("sortBy", params.sortBy);
    if (params.sortOrder) httpParams = httpParams.set("sortOrder", params.sortOrder);

    return this.http
      .get<OffsetPaginationDto<WikiFault>>(environment.baseUrlV2 + "/faultList/wiki", { params: httpParams })
      .pipe(
        this.apiErrorHandler.handle("fault_list"),
        map((response) => ({
          ...response,
          items: response.items.map((fault) => ({
            ...fault,
            manufacturers: fault.manufacturers?.map((manufacturer) => ({
              ...manufacturer,
              label: transformManufacturerName(manufacturer.label),
            })),
          })),
        }))
      );
  }

  /**
   * Gets an empty FaultList object.
   * @returns {FaultList} A FaultList with an empty label.
   */
  public getEmptyFaultList() {
    return new FaultList({ label: "" });
  }

  /**
   * Creates one fault without transmitting the complete fault list.
   * HTTP errors intentionally pass through unchanged so callers can map
   * validation errors to their form controls.
   */
  public createFault(faultListId: string, payload: FaultPayload): Observable<Fault> {
    return this.http
      .post<FaultResponseDto>(`${environment.baseUrlV2}/faultList/${faultListId}/faults`, toFaultPayload(payload))
      .pipe(map(mapFaultResponse));
  }

  /**
   * Replaces one fault without transmitting the complete fault list. The
   * submitted translations are the complete translation set for the fault.
   * HTTP errors intentionally pass through unchanged so callers can map
   * validation errors to their form controls.
   */
  public updateFault(faultId: string, payload: FaultPayload): Observable<Fault> {
    return this.http
      .put<FaultResponseDto>(`${environment.baseUrl}/faults/${faultId}`, toFaultPayload(payload))
      .pipe(map(mapFaultResponse));
  }

  /**
   * Deletes one fault. A successful response is intentionally body-less (204).
   * HTTP errors pass through unchanged for UI-specific handling.
   */
  public deleteFault(faultId: string): Observable<void> {
    return this.http.delete<void>(`${environment.baseUrl}/faults/${faultId}`);
  }

  /**
   * Creates a new fault list.
   * @param {FaultList} faultList The fault list to create.
   * @returns {Observable<Object>} The response from the server as an Observable.
   */
  public postFaultList(faultList: FaultList) {
    return this.http
      .post(environment.baseUrlV2 + "/faultList", faultList)
      .pipe(this.apiErrorHandler.handle("fault_list"));
  }

  /**
   * Updates an existing fault list on the server.
   * Removes the 'id' property before sending the request.
   * @param {FaultList | undefined} faultList - The fault list to update. Must include an 'id' for targeting.
   * @returns {Observable<Object>} The response from the server as an Observable.
   */

  public putFaultList(faultList: FaultList | undefined) {
    const faultListId = faultList?.id;
    let faultListPayload = {};
    if (faultList) {
      const { id: _id, ...payload } = faultList;
      faultListPayload = payload;
    }
    return this.http
      .put(`${environment.baseUrlV2}/faultList/${faultListId}`, faultListPayload)
      .pipe(this.apiErrorHandler.handle("fault_list"));
  }

  /**
   * Deletes a fault list.
   * @param {FaultList} faultList The fault list to delete. Must include an 'id' for targeting.
   * @returns {Observable<Object>} The response from the server as an Observable.
   */
  public deleteFaultList(faultList: FaultList) {
    return this.http
      .delete(`${environment.baseUrlV2}/faultList/${faultList.id}`)
      .pipe(this.apiErrorHandler.handle("fault_list"));
  }
}
